'use client';

import React, { Key, useEffect } from 'react';

import { Translations } from '@backbone.one/bb1-web-components';
import useOfferComparisonStore, {
  fetchAndUpdateOffer,
} from '../store/use-offer-comparison-store';
import useEnergyCalculatorStore, {
  calculationSelector,
} from '@/app/store/use-energy-calculator-store';
import { useShallow } from 'zustand/shallow';
import InvoiceUpload from '@/app/components/invoice-upload';
import useInvoiceUploadStore from '@/lib/invoice-upload/store/use-invoice-upload-store';
import { ReferenceTariffType } from '@/app/types/reference-tariff-type';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import {
  consumptionParam,
  currentTariffCostParam,
  gridOperatorNameParam,
  sizeParam,
  zipCodeParam,
  cityParam,
  streetParam,
  houseNumberParam,
} from '@/app/utils/energy-calculator-params';
import dynamic from 'next/dynamic';
import { OnSubmitEventDetails } from '@/app/types/energy-calculator';
import { Spinner, Tab, Tabs } from '@heroui/react';
import { CustomerType } from '@/lib/offer-service/types/customer-type';
import { eegUserDataTokenParam } from '@/app/utils/eeg-user-data-token-params';
import { customerTypeParam } from '../utils/customer-type-param';

const invoiceUploadParam = 'invoiceUpload';

// Dynamically import the Web Component initializer on the client only.
// This is required because the parameter types (e.g. `value`, `translations`) differ between client and server.
// Setting `ssr: false` ensures the component is only initialized in the browser, where it can handle the correct types.
const WCEnergyCalculator = dynamic(
  () => import('@/app/components/wc-energy-calculator'),
  {
    loading: () => EnergyCalculatorLoader(),
    ssr: false,
  }
);

type EnergyCalculatorProps = {
  apiKey: string;
  isBusinessEnabled: boolean;
  country: string;
  serviceBaseUrl?: string;
};

export default function EnergyCalculator({
  apiKey,
  isBusinessEnabled,
  country,
  serviceBaseUrl,
}: EnergyCalculatorProps) {
  const searchParams = useSearchParams();
  const isAutoInvoiceUpload = searchParams.has(invoiceUploadParam);
  const existingCustomerType = searchParams.get(
    customerTypeParam
  ) as CustomerType | null;
  const t = useTranslations('offerCalculator');
  const tInvoiceUpload = useTranslations('invoiceUpload');
  const isInvoiceLoading = useInvoiceUploadStore((state) => state.isLoading);
  const isOfferLoading = useOfferComparisonStore((state) => state.isLoading);
  const isLoading = isInvoiceLoading || isOfferLoading;
  const customerType = useEnergyCalculatorStore((state) => state.customerType);
  const tabStyles = 'text-lg leading-8 font-medium ';
  const setEnergyCalculatorData = useEnergyCalculatorStore(
    (state) => state.setCalculatorData
  );
  const setCustomerType = useEnergyCalculatorStore(
    (state) => state.setCustomerType
  );
  const calculatorValue = useEnergyCalculatorStore(
    useShallow(calculationSelector)
  );
  const customer = useEnergyCalculatorStore((state) => state.customerType);

  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString());
    [
      sizeParam,
      consumptionParam,
      zipCodeParam,
      cityParam,
      streetParam,
      houseNumberParam,
      gridOperatorNameParam,
      currentTariffCostParam,
      eegUserDataTokenParam,
      customerTypeParam,
    ].forEach((param) => {
      if (params.get(param)) {
        params.delete(param);
      }
    });

    if (isAutoInvoiceUpload) {
      params.delete(invoiceUploadParam);
    }

    window.history.replaceState(null, '', `?${params.toString()}`);
  }, []);

  const onSubmit = (details: OnSubmitEventDetails) => {
    setEnergyCalculatorData(details, ReferenceTariffType.average);
    fetchAndUpdateOffer();
  };

  const onError = (error: unknown) => {
    console.log('Error occurred', error); // todo handle error
  };

  const calculatorTranslations: Partial<Translations> = {
    gridOperator: t('gridOperator.label'),
    gridOperatorInfo: t('gridOperator.info'),
    gridOperatorPlaceholder: t('gridOperator.placeholder'),
    householdSize: t('householdSize'),
    businessSize: t('businessSize'),
    annualConsumption: t('annualConsumption'),
    zip: t('zipLabel'),
    submit: t('submit'),
    small: t('small'),
    medium: t('medium'),
    large: t('large'),
    zipPlaceholder: t('zipPlaceholder'),
  };

  return (
    <div>
      <div
        className="bg-[var(--color-card-primary)] md:border-1 border-solid border-[#D8D8D8] rounded-3.75 min-h-[384px]"
        data-testid="energyCalculatorCard"
      >
        <div className="px-4 pt-4 md:px-8 md:pt-8 pb-0">
          <p
            className="font-heading text-2xl md:text-[28px] leading-8 md:leading-10 pt-10 md:pt-0"
            data-testid="energyCalculatorScreenTitle"
          >
            {t('title')}
          </p>
          <p className="font-medium leading-5 md:leading-6 md:text-xl pt-2 md:pt-4">
            {t('subTitle')}
          </p>
          {isBusinessEnabled && existingCustomerType == null && (
            <Tabs
              data-testid="customer-tabs"
              variant="underlined"
              classNames={{
                base: 'pt-6',
                cursor:
                  'w-full bg-[var(--color-action-secondary)] -bottom-[1px]',
                tabContent: 'pb-3 !text-[var(--color-action-secondary)]',
                tabList:
                  'border-b-1 border-[var(--color-border-default)] p-0 m-1 overflow-visible',
              }}
              fullWidth
              aria-label="customer type options"
              selectedKey={customer}
              onSelectionChange={(value: Key) => {
                const selected = value.toString() as CustomerType;
                setCustomerType(selected);
              }}
            >
              <Tab
                data-testid="energyCalculatorScreenPersonal"
                className={tabStyles}
                key={CustomerType.Home}
                title={t('private')}
              ></Tab>
              <Tab
                data-testid="energyCalculatorBusinessTab"
                className={tabStyles}
                key={CustomerType.Business}
                title={t('business')}
              ></Tab>
            </Tabs>
          )}
        </div>
        <WCEnergyCalculator
          apiKey={apiKey}
          value={calculatorValue}
          translations={calculatorTranslations}
          isLoading={isLoading}
          onSubmit={onSubmit}
          onError={onError}
          customerType={customerType}
          country={country}
          serviceBaseUrl={serviceBaseUrl}
        />
      </div>
      <p className="mt-6 px-4 md:px-0 py-4 text-center font-normal md:font-medium text-sm md:text-xl leading-6.5 bg-[var(--color-card-primary)] md:border-1 boder-solid border-[#D8D8D8] rounded-3.75">
        {tInvoiceUpload.rich('calculator.body', {
          uploadLink: (chunks) => (
            <InvoiceUpload>
              <span
                data-testid="energyCalculatorInvoiceUploadTrigger"
                className="font-bold underline underline-offset-4"
              >
                {chunks}
              </span>
              <Image
                data-testid="upload-icon"
                className="inline-block ml-1.5"
                alt="upload icon"
                src="/images/upload.svg"
                width={15}
                height={15}
              />
            </InvoiceUpload>
          ),
        })}
      </p>
    </div>
  );
}

function EnergyCalculatorLoader() {
  return (
    <div className="flex items-center justify-center py-[150px]">
      <Spinner
        data-testid="spinner"
        size="lg"
        variant="default"
        color="success"
        classNames={{
          circle1: 'h-[80px] w-[80px]',
          circle2: 'h-[80px] w-[80px]',
        }}
      />
    </div>
  );
}
